const DEFAULT_LCD_COLUMNS = 16;
const DEFAULT_LCD_ROWS = 2;
const LCD_DDRAM_COLUMNS_PER_ROW = 40;
const LCD_ROW_START_ADDRESSES = [0x00, 0x40];

const clampPositiveInteger = (value, fallbackValue) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return fallbackValue;
  }

  return Math.max(1, Math.round(numericValue));
};

const createBlankVisibleRow = (columns = DEFAULT_LCD_COLUMNS) => (
  ' '.repeat(clampPositiveInteger(columns, DEFAULT_LCD_COLUMNS))
);

const createBlankVisibleRows = (
  columns = DEFAULT_LCD_COLUMNS,
  rows = DEFAULT_LCD_ROWS
) => (
  Array.from(
    { length: clampPositiveInteger(rows, DEFAULT_LCD_ROWS) },
    () => createBlankVisibleRow(columns)
  )
);

const createBlankDdram = () => (
  Array.from(
    { length: DEFAULT_LCD_ROWS },
    () => Array.from({ length: LCD_DDRAM_COLUMNS_PER_ROW }, () => ' ')
  )
);

const cloneDdram = (ddram) => (
  Array.isArray(ddram)
    ? ddram.map((row) => (Array.isArray(row) ? [...row] : Array.from({ length: LCD_DDRAM_COLUMNS_PER_ROW }, () => ' ')))
    : createBlankDdram()
);

const normalizeAddressToRowColumn = (address) => {
  const safeAddress = Number(address) & 0x7F;

  if (safeAddress >= 0x00 && safeAddress < 0x00 + LCD_DDRAM_COLUMNS_PER_ROW) {
    return {
      rowIndex: 0,
      columnIndex: safeAddress,
    };
  }

  if (safeAddress >= 0x40 && safeAddress < 0x40 + LCD_DDRAM_COLUMNS_PER_ROW) {
    return {
      rowIndex: 1,
      columnIndex: safeAddress - 0x40,
    };
  }

  return null;
};

const normalizeShiftOffset = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  const normalized = Math.round(numericValue) % LCD_DDRAM_COLUMNS_PER_ROW;
  return normalized < 0
    ? normalized + LCD_DDRAM_COLUMNS_PER_ROW
    : normalized;
};

const nextDdramAddress = (address, increment = true) => {
  const safeAddress = Number(address) & 0x7F;

  if (increment) {
    if (safeAddress >= 0x00 && safeAddress < 0x27) {
      return safeAddress + 1;
    }

    if (safeAddress === 0x27) {
      return 0x40;
    }

    if (safeAddress >= 0x40 && safeAddress < 0x67) {
      return safeAddress + 1;
    }

    if (safeAddress === 0x67) {
      return 0x00;
    }

    return (safeAddress + 1) & 0x7F;
  }

  if (safeAddress > 0x00 && safeAddress <= 0x27) {
    return safeAddress - 1;
  }

  if (safeAddress === 0x00) {
    return 0x67;
  }

  if (safeAddress > 0x40 && safeAddress <= 0x67) {
    return safeAddress - 1;
  }

  if (safeAddress === 0x40) {
    return 0x27;
  }

  return (safeAddress - 1) & 0x7F;
};

const writeDdramCharacter = (state, address, character) => {
  const rowColumn = normalizeAddressToRowColumn(address);

  if (!rowColumn) {
    return;
  }

  state.ddram[rowColumn.rowIndex][rowColumn.columnIndex] = character;
};

const buildVisibleRows = (state, { visible = true } = {}) => {
  const columns = clampPositiveInteger(state?.columns, DEFAULT_LCD_COLUMNS);
  const rows = clampPositiveInteger(state?.rows, DEFAULT_LCD_ROWS);

  if (!visible) {
    return createBlankVisibleRows(columns, rows);
  }

  const shiftOffset = normalizeShiftOffset(state?.displayShift);

  return Array.from({ length: rows }, (_, rowIndex) => {
    const ddramRow = Array.isArray(state?.ddram?.[rowIndex])
      ? state.ddram[rowIndex]
      : Array.from({ length: LCD_DDRAM_COLUMNS_PER_ROW }, () => ' ');
    const visibleCharacters = Array.from({ length: columns }, (_, columnIndex) => (
      ddramRow[(columnIndex + shiftOffset) % LCD_DDRAM_COLUMNS_PER_ROW] || ' '
    ));

    return visibleCharacters.join('');
  });
};

const translateCharacterCode = (value) => {
  const safeValue = Number(value) & 0xFF;

  if (safeValue >= 32 && safeValue <= 126) {
    return String.fromCharCode(safeValue);
  }

  if (safeValue === 0xDF) {
    return '\u00B0';
  }

  if (safeValue === 0xFF) {
    return '\u2588';
  }

  return ' ';
};

const createDefaultSignalLevels = () => ({
  d4: false,
  d5: false,
  d6: false,
  d7: false,
  e: false,
  rs: false,
  rw: false,
});

const extractSignalLevels = (signalSnapshot = {}) => ({
  d4: typeof signalSnapshot?.d4 === 'boolean' ? signalSnapshot.d4 : null,
  d5: typeof signalSnapshot?.d5 === 'boolean' ? signalSnapshot.d5 : null,
  d6: typeof signalSnapshot?.d6 === 'boolean' ? signalSnapshot.d6 : null,
  d7: typeof signalSnapshot?.d7 === 'boolean' ? signalSnapshot.d7 : null,
  e: signalSnapshot?.e === true,
  rs: typeof signalSnapshot?.rs === 'boolean' ? signalSnapshot.rs : null,
  rw: typeof signalSnapshot?.rw === 'boolean' ? signalSnapshot.rw : null,
});

const createBaseState = ({ columns, rows } = {}) => ({
  columns: clampPositiveInteger(columns, DEFAULT_LCD_COLUMNS),
  rows: clampPositiveInteger(rows, DEFAULT_LCD_ROWS),
  addressMode: 'ddram',
  blinkOn: false,
  cgramAddress: 0,
  cursorAddress: LCD_ROW_START_ADDRESSES[0],
  cursorOn: false,
  ddram: createBlankDdram(),
  displayOn: true,
  displayShift: 0,
  entryModeIncrement: true,
  entryModeShift: false,
  fourBitMode: false,
  functionSet: {
    dataLength8Bit: true,
    font5x10: false,
    twoLine: true,
  },
  lastCommand: null,
  lastEnableHigh: false,
  pendingNibble: null,
  pendingRegisterSelect: 0,
  signalLevels: createDefaultSignalLevels(),
});

const cloneState = (state) => {
  const baseState = createBaseState({
    columns: state?.columns,
    rows: state?.rows,
  });

  return {
    ...baseState,
    ...state,
    ddram: cloneDdram(state?.ddram),
    functionSet: {
      ...baseState.functionSet,
      ...(state?.functionSet || {}),
    },
    signalLevels: {
      ...createDefaultSignalLevels(),
      ...(state?.signalLevels || {}),
    },
  };
};

const shiftDisplay = (state, delta) => {
  state.displayShift = normalizeShiftOffset(
    normalizeShiftOffset(state.displayShift) + Number(delta || 0)
  );
};

const clearDisplay = (state) => {
  state.ddram = createBlankDdram();
  state.cursorAddress = LCD_ROW_START_ADDRESSES[0];
  state.displayShift = 0;
  state.addressMode = 'ddram';
};

const processCommand = (state, commandByte) => {
  const safeCommand = Number(commandByte) & 0xFF;
  state.lastCommand = safeCommand;

  if (safeCommand === 0x01) {
    clearDisplay(state);
    return;
  }

  if (safeCommand === 0x02) {
    state.cursorAddress = LCD_ROW_START_ADDRESSES[0];
    state.displayShift = 0;
    state.addressMode = 'ddram';
    return;
  }

  if ((safeCommand & 0xFC) === 0x04) {
    state.entryModeIncrement = Boolean(safeCommand & 0x02);
    state.entryModeShift = Boolean(safeCommand & 0x01);
    return;
  }

  if ((safeCommand & 0xF8) === 0x08) {
    state.displayOn = Boolean(safeCommand & 0x04);
    state.cursorOn = Boolean(safeCommand & 0x02);
    state.blinkOn = Boolean(safeCommand & 0x01);
    return;
  }

  if ((safeCommand & 0xF0) === 0x10) {
    const shouldShiftDisplay = Boolean(safeCommand & 0x08);
    const moveRight = Boolean(safeCommand & 0x04);

    if (shouldShiftDisplay) {
      shiftDisplay(state, moveRight ? -1 : 1);
    } else {
      state.cursorAddress = nextDdramAddress(
        state.cursorAddress,
        moveRight
      );
    }

    return;
  }

  if ((safeCommand & 0xE0) === 0x20) {
    state.fourBitMode = (safeCommand & 0x10) === 0;
    state.functionSet = {
      ...state.functionSet,
      dataLength8Bit: Boolean(safeCommand & 0x10),
      twoLine: Boolean(safeCommand & 0x08),
      font5x10: Boolean(safeCommand & 0x04),
    };
    return;
  }

  if ((safeCommand & 0xC0) === 0x40) {
    state.addressMode = 'cgram';
    state.cgramAddress = safeCommand & 0x3F;
    return;
  }

  if (safeCommand & 0x80) {
    state.addressMode = 'ddram';
    state.cursorAddress = safeCommand & 0x7F;
  }
};

const writeDataByte = (state, dataByte) => {
  const safeDataByte = Number(dataByte) & 0xFF;

  if (state.addressMode === 'cgram') {
    state.cgramAddress = (state.cgramAddress + 1) & 0x3F;
    return;
  }

  writeDdramCharacter(
    state,
    state.cursorAddress,
    translateCharacterCode(safeDataByte)
  );

  if (state.entryModeShift) {
    shiftDisplay(state, state.entryModeIncrement ? 1 : -1);
  }

  state.cursorAddress = nextDdramAddress(
    state.cursorAddress,
    state.entryModeIncrement
  );
};

const resolvePotentialLevel = (potentials = []) => {
  const hasHigh = (Array.isArray(potentials) ? potentials : []).some((potential) => potential?.level === 'high');
  const hasLow = (Array.isArray(potentials) ? potentials : []).some((potential) => potential?.level === 'low');

  if (hasHigh && !hasLow) {
    return true;
  }

  if (hasLow && !hasHigh) {
    return false;
  }

  return null;
};

const resolveSignalBoolean = (source = {}, simulationPinStates = {}) => {
  const pinName = String(source?.pinName || '').trim();

  if (pinName) {
    const pinState = simulationPinStates?.[pinName];

    if (pinState) {
      if (typeof pinState.sampledHigh === 'boolean') {
        return pinState.sampledHigh;
      }

      if (typeof pinState.isHigh === 'boolean') {
        return pinState.isHigh;
      }

      if (String(pinState.logicLevel || '').trim().toLowerCase() === 'high') {
        return true;
      }

      if (String(pinState.logicLevel || '').trim().toLowerCase() === 'low') {
        return false;
      }
    }
  }

  return resolvePotentialLevel(source?.potentials || []);
};

export const createLcd16x2SimulationState = (parameters = {}) => (
  createBaseState(parameters)
);

export const getBlankLcd16x2VisibleRows = ({
  columns = DEFAULT_LCD_COLUMNS,
  rows = DEFAULT_LCD_ROWS,
} = {}) => (
  createBlankVisibleRows(columns, rows)
);

export const getLcd16x2VisibleRows = (
  state,
  { powered = true, displayOn = true } = {}
) => (
  buildVisibleRows(state, { visible: Boolean(powered) && Boolean(displayOn) })
);

export const buildLcd16x2SignalSnapshot = (
  lcdAnalysis,
  simulationPinStates = {}
) => {
  const signals = lcdAnalysis?.signals || {};
  const rs = resolveSignalBoolean(signals.rs, simulationPinStates);
  const rw = resolveSignalBoolean(signals.rw, simulationPinStates);
  const e = resolveSignalBoolean(signals.e, simulationPinStates);
  const d4 = resolveSignalBoolean(signals.d4, simulationPinStates);
  const d5 = resolveSignalBoolean(signals.d5, simulationPinStates);
  const d6 = resolveSignalBoolean(signals.d6, simulationPinStates);
  const d7 = resolveSignalBoolean(signals.d7, simulationPinStates);
  const backlightAnode = resolveSignalBoolean(signals.backlightAnode, simulationPinStates);
  const backlightCathode = resolveSignalBoolean(signals.backlightCathode, simulationPinStates);

  return {
    backlightOn: Boolean(lcdAnalysis?.isPowered) &&
      backlightAnode === true &&
      backlightCathode === false,
    columns: clampPositiveInteger(
      lcdAnalysis?.parameters?.columns,
      DEFAULT_LCD_COLUMNS
    ),
    d4,
    d5,
    d6,
    d7,
    e,
    isPowered: Boolean(lcdAnalysis?.isPowered),
    rs,
    rows: clampPositiveInteger(
      lcdAnalysis?.parameters?.rows,
      DEFAULT_LCD_ROWS
    ),
    rw,
  };
};

const applyLcd16x2SignalSnapshot = (
  currentState,
  signalSnapshot = {}
) => {
  const nextState = cloneState(
    currentState ||
      createBaseState({
        columns: signalSnapshot?.columns,
        rows: signalSnapshot?.rows,
      })
  );
  const nextSignalLevels = extractSignalLevels(signalSnapshot);

  nextState.columns = clampPositiveInteger(
    signalSnapshot?.columns,
    nextState.columns
  );
  nextState.rows = clampPositiveInteger(
    signalSnapshot?.rows,
    nextState.rows
  );

  const enableHigh = signalSnapshot?.e === true;

  if (!signalSnapshot?.isPowered) {
    const resetState = createBaseState({
      columns: nextState.columns,
      rows: nextState.rows,
    });
    resetState.lastEnableHigh = enableHigh;
    resetState.signalLevels = nextSignalLevels;
    return resetState;
  }

  const latchedNibble = (
    [signalSnapshot?.d4, signalSnapshot?.d5, signalSnapshot?.d6, signalSnapshot?.d7].every(
      (bit) => typeof bit === 'boolean'
    )
  )
    ? (
        (signalSnapshot.d4 ? 0x1 : 0x0) |
        (signalSnapshot.d5 ? 0x2 : 0x0) |
        (signalSnapshot.d6 ? 0x4 : 0x0) |
        (signalSnapshot.d7 ? 0x8 : 0x0)
      )
    : null;
  const hasFallingEdge = nextState.lastEnableHigh && !enableHigh;

  nextState.lastEnableHigh = enableHigh;
  nextState.signalLevels = nextSignalLevels;

  if (!hasFallingEdge || signalSnapshot?.rw !== false || latchedNibble === null) {
    if (signalSnapshot?.rw === true) {
      nextState.pendingNibble = null;
    }

    return nextState;
  }

  if (!nextState.fourBitMode) {
    if (signalSnapshot?.rs === false && latchedNibble === 0x2) {
      nextState.fourBitMode = true;
      nextState.functionSet = {
        ...nextState.functionSet,
        dataLength8Bit: false,
      };
    }

    nextState.pendingNibble = null;
    return nextState;
  }

  if (signalSnapshot?.rs !== true && signalSnapshot?.rs !== false) {
    nextState.pendingNibble = null;
    return nextState;
  }

  if (nextState.pendingNibble === null) {
    nextState.pendingNibble = latchedNibble;
    nextState.pendingRegisterSelect = signalSnapshot.rs ? 1 : 0;
    return nextState;
  }

  const byteValue = ((nextState.pendingNibble & 0x0F) << 4) | (latchedNibble & 0x0F);
  const isDataRegister = nextState.pendingRegisterSelect === 1;

  nextState.pendingNibble = null;

  if (isDataRegister) {
    writeDataByte(nextState, byteValue);
  } else {
    processCommand(nextState, byteValue);
  }

  return nextState;
};

const buildPinSignalMap = (lcdAnalysis) => {
  const signalSources = lcdAnalysis?.signals || {};
  const signalKeys = ['rs', 'rw', 'e', 'd4', 'd5', 'd6', 'd7'];

  return signalKeys.reduce((map, signalKey) => {
    const pinName = String(signalSources?.[signalKey]?.pinName || '').trim();

    if (!pinName) {
      return map;
    }

    if (!Array.isArray(map[pinName])) {
      map[pinName] = [];
    }

    map[pinName].push(signalKey);
    return map;
  }, {});
};

const resolveTransitionEventBoolean = (event = {}) => {
  if (typeof event?.sampledHigh === 'boolean') {
    return event.sampledHigh;
  }

  if (typeof event?.isHigh === 'boolean') {
    return event.isHigh;
  }

  const logicLevel = String(event?.logicLevel || '').trim().toLowerCase();

  if (logicLevel === 'high') {
    return true;
  }

  if (logicLevel === 'low') {
    return false;
  }

  return null;
};

/* const groupPinTransitionEventsByCycle = (pinTransitionEvents = []) => {
  const normalizedEvents = (Array.isArray(pinTransitionEvents) ? pinTransitionEvents : [])
    .filter((event) => event && typeof event === 'object')
    .sort((left, right) => {
      const leftCycles = Number(left?.cpuCycles);
      const rightCycles = Number(right?.cpuCycles);

      if (leftCycles !== rightCycles) {
        return leftCycles - rightCycles;
      }

      return Number(left?.sequence || 0) - Number(right?.sequence || 0);
    });

  return normalizedEvents.reduce((groups, event) => {
    const currentCycles = Number(event?.cpuCycles);
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || Number(lastGroup.cpuCycles) !== currentCycles) {
      groups.push({
        cpuCycles: currentCycles,
        events: [event],
      });
      return groups;
    }

    lastGroup.events.push(event);
    return groups;
  }, []);
}; */

const sortPinTransitionEvents = (pinTransitionEvents = []) => (
  (Array.isArray(pinTransitionEvents) ? pinTransitionEvents : [])
    .filter((event) => event && typeof event === 'object')
    .sort((left, right) => {
      const leftCycles = Number(left?.cpuCycles);
      const rightCycles = Number(right?.cpuCycles);

      if (leftCycles !== rightCycles) {
        return leftCycles - rightCycles;
      }

      return Number(left?.sequence || 0) - Number(right?.sequence || 0);
    })
);

const buildEventReplaySnapshot = (
  state,
  fallbackSignalSnapshot = {},
  pinSignalMap = {}
) => {
  const signalLevels = {
    ...createDefaultSignalLevels(),
    ...(state?.signalLevels || {}),
  };
  const trackedSignalKeys = new Set(
    Object.values(pinSignalMap).flatMap((signalKeys) => (
      Array.isArray(signalKeys) ? signalKeys : []
    ))
  );
  const resolveSignalValue = (signalKey) => (
    trackedSignalKeys.has(signalKey)
      ? signalLevels[signalKey]
      : signalLevels[signalKey] ?? fallbackSignalSnapshot[signalKey]
  );

  return {
    ...fallbackSignalSnapshot,
    d4: resolveSignalValue('d4'),
    d5: resolveSignalValue('d5'),
    d6: resolveSignalValue('d6'),
    d7: resolveSignalValue('d7'),
    e: resolveSignalValue('e'),
    rs: resolveSignalValue('rs'),
    rw: resolveSignalValue('rw'),
  };
};

export const advanceLcd16x2Simulation = (
  currentState,
  signalSnapshot = {}
) => (
  applyLcd16x2SignalSnapshot(currentState, signalSnapshot)
);

export const advanceLcd16x2SimulationFromPinTransitionEvents = (
  currentState,
  lcdAnalysis,
  simulationPinStates = {},
  pinTransitionEvents = []
) => {
  const finalSignalSnapshot = buildLcd16x2SignalSnapshot(
    lcdAnalysis,
    simulationPinStates
  );
  const pinSignalMap = buildPinSignalMap(lcdAnalysis);
  const sortedEvents = sortPinTransitionEvents(pinTransitionEvents);

  let nextState = currentState;

    if (
    finalSignalSnapshot.isPowered &&
    sortedEvents.length > 0 &&
    Object.keys(pinSignalMap).length > 0
  ) {
    let replaySignalSnapshot = buildEventReplaySnapshot(
      currentState,
      finalSignalSnapshot,
      pinSignalMap
    );

    sortedEvents.forEach((event) => {
      const signalKeys = pinSignalMap[String(event?.pinName || '').trim()] || [];
      const nextLevel = resolveTransitionEventBoolean(event);

      if (nextLevel === null || signalKeys.length === 0) {
        return;
      }

      let didChangeSignal = false;
      const nextReplaySignalSnapshot = {
        ...replaySignalSnapshot,
      };

      signalKeys.forEach((signalKey) => {
        if (nextReplaySignalSnapshot[signalKey] === nextLevel) {
          return;
        }

        nextReplaySignalSnapshot[signalKey] = nextLevel;
        didChangeSignal = true;
      });

      if (!didChangeSignal) {
        return;
      }

      nextState = applyLcd16x2SignalSnapshot(
        nextState,
        nextReplaySignalSnapshot
      );

      replaySignalSnapshot = buildEventReplaySnapshot(
        nextState,
        nextReplaySignalSnapshot,
        pinSignalMap
      );
    });

    return nextState;
  }

  return applyLcd16x2SignalSnapshot(nextState, finalSignalSnapshot);
};

/* export const advanceLcd16x2SimulationFromPinTransitionEvents = (
  currentState,
  lcdAnalysis,
  simulationPinStates = {},
  pinTransitionEvents = []
) => {
  const finalSignalSnapshot = buildLcd16x2SignalSnapshot(
    lcdAnalysis,
    simulationPinStates
  );
  const pinSignalMap = buildPinSignalMap(lcdAnalysis);
  const eventGroups = groupPinTransitionEventsByCycle(pinTransitionEvents);
  let nextState = currentState;

  if (finalSignalSnapshot.isPowered && eventGroups.length > 0 && Object.keys(pinSignalMap).length > 0) {
    let replaySignalSnapshot = buildEventReplaySnapshot(
      currentState,
      finalSignalSnapshot,
      pinSignalMap
    );

    eventGroups.forEach((eventGroup) => {
      let didChangeSignal = false;
      const nextReplaySignalSnapshot = {
        ...replaySignalSnapshot,
      };

      (Array.isArray(eventGroup?.events) ? eventGroup.events : []).forEach((event) => {
        const signalKeys = pinSignalMap[String(event?.pinName || '').trim()] || [];
        const nextLevel = resolveTransitionEventBoolean(event);

        if (nextLevel === null || signalKeys.length === 0) {
          return;
        }

        signalKeys.forEach((signalKey) => {
          if (nextReplaySignalSnapshot[signalKey] === nextLevel) {
            return;
          }

          nextReplaySignalSnapshot[signalKey] = nextLevel;
          didChangeSignal = true;
        });
      });

      if (!didChangeSignal) {
        return;
      }

      nextState = applyLcd16x2SignalSnapshot(
        nextState,
        nextReplaySignalSnapshot
      );
      replaySignalSnapshot = buildEventReplaySnapshot(
        nextState,
        nextReplaySignalSnapshot,
        pinSignalMap
      );
    });
  }

  return applyLcd16x2SignalSnapshot(nextState, finalSignalSnapshot);
};
 */